/**
 * Penticton.dev Brand Configuration
 *
 * Central configuration for brand identity synchronization between
 * the main penticton.dev site and the events platform.
 *
 * This module ensures consistent messaging, contact information,
 * and visual identity across all Penticton.dev digital properties.
 */
// Community contact information synchronized with penticton.dev
export const businessContact = {
    email: 'info@penticton.dev',
    phone: '+1-250-555-0100',
    website: 'https://penticton.dev',
    // Community event times in PST/PDT (Penticton timezone)
    businessHours: {
        timezone: 'America/Vancouver',
        schedule: {
            monday: { open: '09:00', close: '17:00' },
            tuesday: { open: '09:00', close: '17:00' },
            wednesday: { open: '09:00', close: '17:00' },
            thursday: { open: '09:00', close: '17:00' },
            friday: { open: '09:00', close: '17:00' },
            saturday: null, // Closed
            sunday: null, // Closed
        },
        display: 'Monday-Friday: 9:00 AM - 5:00 PM PST',
    },
};
// Platform configuration for Events subdomain
export const platformConfig = {
    // Events platform domain configuration - fallback to default for server-side environments
    baseUrl: 'https://events.penticton.dev',
    name: 'Penticton.dev Events',
    canonical: 'events.penticton.dev',
    // Platform branding
    title: 'Penticton.dev Events - Developer Community & Networking',
    description: 'Official developer events platform for Penticton.dev. Join local tech meetups, workshops, and networking events in the Okanagan Valley.',
    // Environment fallback for development
    fallbackBaseUrl: 'https://penticton.dev',
};
// Community location
export const businessLocation = {
    city: 'Penticton',
    province: 'BC',
    country: 'Canada',
    coordinates: {
        latitude: 49.4991,
        longitude: -119.5937,
    },
    communityRadius: 50, // km
    postalCode: 'V2A', // General area postal code
    region: 'Okanagan Valley',
};
// Brand messaging synchronized with main penticton.dev site
export const brandMessaging = {
    // Primary value propositions
    headline: 'Connect with Penticton\'s Developer Community',
    subheadline: 'Join local tech meetups, share knowledge, and grow together',
    tagline: 'Building community in the Okanagan Valley',
    // Community activities
    communityActivities: {
        meetups: 'Regular tech meetups and workshops',
        networking: 'Connect with local developers',
        learning: 'Share knowledge and learn new skills',
        collaboration: 'Build projects together',
    },
    // Community messaging for events platform
    community: {
        welcome: 'Welcome to Penticton.dev Events',
        description: 'Building a vibrant developer community in the beautiful Okanagan Valley. Connect, learn, and grow together.',
        callToAction: 'Join the thriving developer community in Penticton, BC',
        mission: 'Connect with local developers, attend exciting events, and grow your skills together',
    },
    // SEO and meta descriptions
    seo: {
        title: 'Penticton.dev Events - Local Developer Community & Tech Meetups',
        description: 'Join Penticton\'s thriving tech community. Discover local developer meetups, workshops, and networking events. Connect, learn, and grow with fellow developers in the Okanagan Valley.',
        keywords: [
            'penticton developer community',
            'tech meetups penticton',
            'developer events okanagan',
            'programming community bc',
            'tech networking penticton',
            'coding meetups okanagan valley',
            'developer groups penticton',
            'tech talks penticton',
            'programming workshops bc',
        ],
    },
};
// Social media and external links
export const socialLinks = {
    github: {
        url: 'https://github.com/zss1980',
        handle: '@zss1980',
        display: 'GitHub',
    },
    linkedin: {
        url: 'https://www.linkedin.com/company/penticton-dev',
        handle: 'penticton-dev',
        display: 'LinkedIn',
    },
};
// Visual brand assets and styling
export const brandAssets = {
    logo: {
        // Logo variants for different contexts
        primary: '/logo.svg',
        wordmark: '/wordmark.svg',
        favicon: '/favicon.svg',
        // Alternative logo URL from main site
        fallback: 'https://penticton.dev/android-chrome-512x512.png',
    },
    // Brand colors - including the gold accent requirement
    colors: {
        // Gold accent colors for Story 22 requirement
        gold: {
            primary: '#FFD700', // Pure gold
            secondary: '#FFC107', // Material-UI compatible amber
            accessible: '#F9A825', // WCAG AAA compliant for light backgrounds
            variants: {
                50: '#FFFDE7',
                100: '#FFF9C4',
                200: '#FFF59D',
                300: '#FFF176',
                400: '#FFEE58',
                500: '#FFEB3B',
                600: '#FDD835',
                700: '#FBC02D',
                800: '#F9A825',
                900: '#F57F17',
            },
        },
        // Maintain existing brand palette
        pacific: '#2196f3',
        sunset: '#fbbd00',
    },
    // Typography preference from penticton.dev
    typography: {
        primary: 'Noto Sans',
        fallback: [
            'Inter',
            'IBM Plex Sans',
            'system-ui',
            '-apple-system',
            'BlinkMacSystemFont',
            'Segoe UI',
            'Roboto',
            'Arial',
            'sans-serif',
        ],
    },
};
// Organizer profile configuration
export const organizerProfile = {
    name: 'Penticton.dev',
    title: 'Community Organizer & Tech Advocate',
    description: 'Building a vibrant developer community in Penticton through regular meetups, workshops, and networking events. Passionate about connecting local tech enthusiasts and fostering knowledge sharing.',
    contact: businessContact,
    location: businessLocation,
    social: socialLinks,
    // Professional details
    expertise: [
        'Community Building',
        'Event Organization',
        'Developer Networking',
        'Tech Education',
        'Open Source Advocacy',
    ],
};
// Structured data schema constants for SEO
export const structuredData = {
    organization: {
        '@type': 'Organization',
        name: 'Penticton.dev',
        url: businessContact.website,
        logo: brandAssets.logo.fallback,
        contactPoint: {
            '@type': 'ContactPoint',
            telephone: businessContact.phone,
            contactType: 'Community Support',
            email: businessContact.email,
            availableLanguage: 'English',
        },
        address: {
            '@type': 'PostalAddress',
            addressLocality: businessLocation.city,
            addressRegion: businessLocation.province,
            addressCountry: businessLocation.country,
            postalCode: businessLocation.postalCode,
        },
        geo: {
            '@type': 'GeoCoordinates',
            latitude: businessLocation.coordinates.latitude,
            longitude: businessLocation.coordinates.longitude,
        },
        sameAs: [
            socialLinks.github.url,
            socialLinks.linkedin.url,
        ],
    },
};
// Utility functions for brand integration
export const getBrandColor = (variant, shade) => {
    if (variant === 'gold') {
        return shade ? brandAssets.colors.gold.variants[shade] : brandAssets.colors.gold.primary;
    }
    return brandAssets.colors[variant];
};
export const getBusinessHours = (day) => {
    if (!day)
        return businessContact.businessHours.display;
    const dayLower = day.toLowerCase();
    const hours = businessContact.businessHours.schedule[dayLower];
    if (!hours)
        return 'Closed';
    return `${hours.open} - ${hours.close}`;
};
export const formatContactInfo = (includeHours = true) => {
    const contact = [
        businessContact.email,
        businessContact.phone,
    ];
    if (includeHours) {
        contact.push(businessContact.businessHours.display);
    }
    return contact;
};
// Additional utility functions for gold accent colors
export const getGoldColor = (shade, alpha) => {
    const color = shade === 'primary'
        ? brandAssets.colors.gold.primary
        : shade === 'secondary'
            ? brandAssets.colors.gold.secondary
            : shade === 'accessible'
                ? brandAssets.colors.gold.accessible
                : brandAssets.colors.gold.variants[shade];
    return alpha ? `${color}${Math.round(alpha * 255).toString(16).padStart(2, '0').toUpperCase()}` : color;
};
export const getAccessibleGoldColor = (mode) => {
    return mode === 'light' ? '#F9A825' : brandAssets.colors.gold.variants[300];
};
// Export all configuration as a single brand object
export const brand = {
    contact: businessContact,
    platform: platformConfig,
    location: businessLocation,
    messaging: brandMessaging,
    social: socialLinks,
    assets: brandAssets,
    organizer: organizerProfile,
    schema: structuredData,
};
export default brand;
